import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ViewerComponent } from "../viewer/viewer.component";
import { MatListModule } from '@angular/material/list';
import { MatTreeModule } from '@angular/material/tree';
import { signOut } from '@aws-amplify/auth';
import { User } from '../master-data/_types/user';
import { UserService } from '../_services/user/user.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from "@angular/material/chips";
import { PermissionService } from '../_services/permission/permission.service';
import { UserManagement } from '../plant-management/_types/user-management';

export interface MenuItem {
  text: string;
  link?: string;
  children?: MenuItem[];
  icon?: string;
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatToolbarModule,
    RouterLink,
    MatSidenavModule,
    ViewerComponent,
    MatListModule,
    MatTreeModule,
    MatProgressSpinnerModule,
    MatChipsModule
],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent implements OnInit {

  loading: boolean = false;
  isCoordinator: boolean = false;
  user!: User | null;
  titleAppForUsers= 'D-BUS';
  titleForCoordinators = ' Bus management Tool ';
  currentUserPermission: UserManagement | null = null;

  @ViewChildren(MatMenu) menus!: QueryList<MatMenu>;
  isMobile: boolean = false;
  activeUrl: string = "";
  isGovernance: boolean = false; 

  constructor(private cdr: ChangeDetectorRef, 
              private userService: UserService,
              private router: Router,
              public permissionService: PermissionService) {
  }

  ngOnInit() {
    this.loading = true;
    this.userService.user$.subscribe(user => {
      this.user = user;
      this.isCoordinator = this.user?.roles?.includes('BusManagement_Coordinators') || false;
      this.isGovernance = this.user?.roles?.includes('BusManagement_Governance') || false;
      this.loading = false;
      if(this.isGovernance){
          if(this.user?.email)
            this.getUserPermissions(this.user?.email, true);
      
      }
      else if(this.isCoordinator ) {
        this.getUserPermissions(this.user?.email || '');
      }
    }, 
  error => {
      console.error('Error fetching user information:', error);
      this.loading = false;
  });
    this.isMobile = this.isMobileDevice();
    this.cdr.detectChanges();
    this.activeUrl = this.router.url;
    this.router.events.subscribe(() => {
      this.activeUrl = this.router.url;
    });
  }
  
  links: MenuItem[] = [
    {
      text: 'Settings',
      icon: 'settings',
      link: '/secure/settings',
      children: [
        { text: 'Plant Configuration', link: '/secure/settings/plant-list' },
        { text: 'Access Management', link: '/secure/settings/access-management' },
      ]
    },
    {
      text: 'Master Data',
      icon: 'folder_open',
      link: '/secure/master-data',
      children: [
        { text: 'Regions', link: '/secure/master-data/region-list' },
        { text: 'Countries', link: '/secure/master-data/country-list' },
        { text: 'Plants', link: '/secure/master-data/plant-list' },
        { text: 'Vehicles', link: '/secure/master-data/vehicle-list' },
        { text: 'Suppliers', link: '/secure/master-data/supplier-list' },
        { text: 'Bus Lines', link: '/secure/master-data/bus-line-list' },
        { text: 'Bus Stops', link: '/secure/master-data/bus-stop-list' },
        { text: 'Price Sheet', link: '/secure/master-data/price-sheet-list' },
      ]
    }, {
      text: 'Call Off',
      icon: 'departure_board',
      link: '/secure/call-off',
      children: [
        { text: 'Call Off List', link: '/secure/call-off-list' },
        { text: 'New Call Off', link: '/secure/call-off/call-off-creation' },
        { text: 'Overview', link: '/secure/call-off/overview' },
      
      ]
    },
    {
      text: 'Bus Entry',
      icon: 'table_view',
      link: '/secure/overview',
      children: [
        { text: 'Bus Arrival', link: '/secure/overview/bus-arrival' },
        { text: 'Employee BusLine', link: '/secure/overview/employee-bus-line' },
        { text: 'Occupancy Rate', link: '/secure/overview/occupancy-rate' },
        { text: 'Bus Entry Report', link: '/secure/call-off/bus-entry-report' },
      ]
    },
    {
      text: 'Route Planning',
      icon: 'map',
      link: '/secure/route-planning',
      children: [
        { text: 'Home', link: '/secure/route-planning/home' },
        { text: 'View All Routes', link: '/secure/route-planning/all-routes-map' },
        { text: 'Employee List', link: '/secure/route-planning/route-builder-from-file' },
        { text: 'Special Routes', link: '/secure/route-planning/special-routes' }
      ]
    },
    {
      text: 'Bus Checker',
      icon: 'checklist',
      link: '/secure/bus-checker',
      children: [
        { text: 'Master Template', link: '/secure/bus-checker/bus-check-list' },
        { text: 'Overview', link: '/secure/bus-checker/bus-check-overview' },
        { text: 'Bus Check', link: '/secure/bus-checker/bus-check-start-options' },
      ]
    },
    {
      text: 'Employee Portal',
      icon: 'person',
      link: '/secure/employee',
      children: [
        { text: 'Home', link: '/secure/employee/employee-portal-home' },
        { text: 'Bus Line Info', link: '/secure/employee/bus-line-info' },
        { text: 'Book Bus Route', link: '/secure/employee/book-bus-route' },
        { text: "Overview Bookings", link: '/secure/employee/overview-booked-routes' },
        { text: 'My Bookings', link: '/secure/employee/book-bus-route-overview-employee' },
        { text: 'Bus Tracking', link: '/secure/employee/bus-tracking' },
        { text: 'GPS Overview', link: '/secure/employee/gps-overview' }
      ]
    },
  ];

  getMenu(index: number): MatMenu | null {
    return this.menus?.toArray()[index] || null;
  }
  childrenAccessor = (node: MenuItem) => node.children ?? [];

  hasChild = (_: number, node: MenuItem) => !!node.children && node.children.length > 0;

  logOut() {
    signOut().then(() => {
      window.location.href = '/';
    }).catch((error: Error) => {
      console.error('Error signing out:', error);
    });
  }

  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  }

  getUserPermissions(email: string, isGovernance: boolean = false) {
    this.loading = true;
    this.permissionService.getUserPermission(email, isGovernance, this.user?.name).subscribe(permission =>{
        this.currentUserPermission = permission;
        this.loading = false;
    },
    error => {
        console.error('Error fetching user permissions:', error);
        this.loading = false;
    });
}
}